import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBlockStore } from '@/store/blockStore';
import {
  outdatedRowVersion,
  useProductVersionStore,
  type OutdatedRowVersion,
} from '@/store/productVersionStore';
import productApi from '@/lib/productApi';
import type { Block, InvestmentBlockContent, InvestmentRow } from '@/types';
import type { QuoteRowStatus } from '@/types/product';

const SYNC_DEBOUNCE_MS = 900;

interface LinkedRow {
  blockId: string;
  row: InvestmentRow;
}

export interface OutdatedQuoteRow extends OutdatedRowVersion {
  rowId: string;
  productId: string;
  hasOverrides: boolean;
}

/** Alle offerteregels die aan een product gekoppeld zijn, over alle blokken heen. */
function collectLinkedRows(blocks: Block[]): LinkedRow[] {
  const linked: LinkedRow[] = [];
  for (const block of blocks) {
    const content = block.content as InvestmentBlockContent | undefined;
    for (const table of content?.tables ?? []) {
      for (const row of table.rows ?? []) {
        if (row.productId && row.productVersionId) linked.push({ blockId: block.id, row });
      }
    }
  }
  return linked;
}

const toCents = (value: number | undefined) => Math.round((value ?? 0) * 100);

export async function syncQuoteProductUsages(quoteId: string, blocks: Block[]) {
  const linked = collectLinkedRows(blocks);
  const byBlock = new Map<string, LinkedRow[]>();
  for (const entry of linked) {
    byBlock.set(entry.blockId, [...(byBlock.get(entry.blockId) ?? []), entry]);
  }

  return productApi.syncQuoteUsages(
    quoteId,
    [...byBlock].map(([blockId, entries]) => ({
      blockId,
      rows: entries.map(({ row }) => ({
        rowId: row.id,
        productId: row.productId,
        productVersionId: row.productVersionId,
        variantId: row.variantId,
        quantity: row.quantity ?? 1,
        unitPriceCents: toCents(row.subtotal),
        discountPct: row.discountPct ?? null,
        totalCents: toCents(row.subtotal),
        hoursSnapshot: row.hours != null ? { total: row.hours } : null,
        hasOverrides: row.hasOverrides ?? false,
      })),
    })),
  );
}

/**
 * Houdt de koppeling tussen offerteregels en producten bij, en meldt welke
 * regels op een oudere productversie staan.
 *
 * De offerte verandert nergens automatisch: de regel houdt zijn eigen bedragen
 * en uren. Dit levert alleen het signaal dat er een nieuwere versie is, zodat
 * sales zelf kan besluiten om bij te werken. De nieuwste versies gaan ook naar
 * useProductVersionStore; daar leest de editor de waarschuwing per regel uit.
 */
export function useQuoteProductStatus(quoteId: string) {
  const blocks = useBlockStore((state) => state.blocks);
  const [statuses, setStatuses] = useState<QuoteRowStatus[]>([]);
  const timer = useRef<number>();
  const lastPayload = useRef<string>('');

  const sync = useCallback(async () => {
    const linked = collectLinkedRows(blocks);

    const byBlock = new Map<string, LinkedRow[]>();
    for (const entry of linked) {
      byBlock.set(entry.blockId, [...(byBlock.get(entry.blockId) ?? []), entry]);
    }

    const payload = JSON.stringify(
      linked.map((entry) => [entry.blockId, entry.row.id, entry.row.productVersionId, entry.row.subtotal, entry.row.quantity, entry.row.discountPct, entry.row.hasOverrides]),
    );
    if (payload === lastPayload.current) return;
    lastPayload.current = payload;

    try {
      const latest = await syncQuoteProductUsages(quoteId, blocks);
      setStatuses(latest);
    } catch {
      // Stil falen: dit is een signaal naast de offerte, geen blokkade voor het bewerken.
    }
  }, [blocks, quoteId]);

  useEffect(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void sync(), SYNC_DEBOUNCE_MS);
    return () => window.clearTimeout(timer.current);
  }, [sync]);

  useEffect(() => {
    productApi
      .quoteStatus(quoteId)
      .then(setStatuses)
      .catch(() => setStatuses([]));
  }, [quoteId]);

  const setVersionStatuses = useProductVersionStore((state) => state.setStatuses);
  const latest = useProductVersionStore((state) => state.latest);
  const versionNumbers = useProductVersionStore((state) => state.versionNumbers);

  useEffect(() => setVersionStatuses(statuses), [statuses, setVersionStatuses]);
  useEffect(() => () => useProductVersionStore.getState().reset(), [quoteId]);

  // Uit de regels zelf, net als het icoon in de tabel: een bijgewerkte of
  // genegeerde regel valt hier meteen af, niet pas na de volgende sync.
  const outdated = useMemo(
    () =>
      collectLinkedRows(blocks).flatMap(({ row }): OutdatedQuoteRow[] => {
        const version = outdatedRowVersion({ latest, versionNumbers }, row.productId, row.productVersionId);
        if (!version || !row.productId) return [];
        return [
          { ...version, rowId: row.id, productId: row.productId, hasOverrides: row.hasOverrides ?? false },
        ];
      }),
    [blocks, latest, versionNumbers]
  );
  const unavailable = statuses.filter(
    (status) => !status.productEnabled || status.productArchived,
  );

  return { statuses, outdated, unavailable, refresh: sync };
}

export default useQuoteProductStatus;
